import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import withStatechart from 'react-automata/lib/withStatechart';
import { withRouter } from 'core/libs/router';
import { matchPath } from 'react-router';
import {
  author as authorRoute,
  about as aboutRoute,
  offline as offlineRoute,
} from 'core/components/BaseRoutes/routes-patterns';

import PubSub from 'pubsub-js';
import cx from 'classnames';

import { renderNothing, branch, compose, getContext } from 'core/libs/recompose';

import withPuids from 'core/hocs/withPuids';
import withRequestId from 'core/hocs/withRequestId';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withRequestHeaders from 'core/hocs/withRequestHeaders';

import initAds from 'core/utils/init-ads';
import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { getCookie } from 'core/utils/cookie-helper';

import adsPropTypes, { puidsPropTypes } from 'core/utils/prop-types/ads';
import requestHeadersPropTypes from 'core/utils/prop-types/request-headers';

import withResolveProps from './resolve/props';
import withResolveDomId from './resolve/domid';
import { withPageNumbers } from './resolve/pageNumbers';

import { SHTORKA_SHOWED, BRANDING_CLASSNAME, USER_ID_COOKIE } from 'core/site/constants';
import styles from './index.styl';
import { isError4xx } from 'core/utils/app-status-helper';

/**
 * Не забывать синхронизировать константы с `capirs-fast-init.js`.
 *
 * TODO: решить проблему с транспайлингом и бандлингом `capirs-fast-init.js`
 * чтоб избавится от проблемы с дублированием.
 */
export const DISABLE_ATTR_NAME = 'data-disable';
export const STATE_ATTR_NAME = 'data-state';
export const OPTIONS_ATTR_NAME = 'data-capirs';
export const RENDER_STATE_ATTR_NAME = 'data-render-state';
export const PREVENT_EXTERNAL_ATTR_NAME = 'data-prevent-external-processing';
export const NAME_ATTR_NAME = 'data-name';

// Состояния стейт машины.
export const IDLE = 'idle';
export const PENDING = 'pending';
export const RENDERED = 'rendered';
export const NOT_EXIST = 'not_exist';
export const UNMOUNTED = 'unmounted';
export const INITIALIZING = 'initializing';
export const RELOADING_RENDERED = 'reloading_rendered';
export const RELOADING_NOT_EXIST = 'reloading_not_exist';
export const REINITIALIZING_RENDERED = 'reinitializing_rendered';
export const REINITIALIZING_NOT_EXIST = 'reinitializing_not_exist';

// События переходов стейт машины.
const WAIT = 'wait';
const INIT = 'init';
const REINIT = 'reinit';
const RELOAD = 'reload';
const RENDER = 'render';
const RESIZE = 'resize';
const UNEXIST = 'unexist';
const UNMOUNT = 'unmount';

/**
 * Схема переходов.
 *
 * По ней можно легко понять в какие состояния может перейти компонент при тех
 * или иных действиях.
 */
const statechart = {
  initial: IDLE,
  states: {
    /**
     * Изначальное состояние, с которым стартует компонент.
     *
     * В нем мы еще не знаем про то, в каком состоянии находится подконтрольное
     * компоненту рекламное место. Это еще предстоит узнать в `initRuntime`.
     */
    [IDLE]: {
      on: {
        [WAIT]: PENDING,
        [INIT]: INITIALIZING,
        [UNMOUNT]: UNMOUNTED,

        /**
         * Для синхронизации состояния с внешним источником используем
         * не события, а сами состояния.
         */
        [PENDING]: PENDING,
        [RENDERED]: RENDERED,
        [NOT_EXIST]: NOT_EXIST,
      },
    },

    /**
     * Состояние ожидания инициализации подконтрольного рекламного места,
     * запущенное извне. Скорее всего в `capirs-fast-init.js`
     *
     * Тут мы подписываемся на события об инициализации, но саму инициализацию
     * не производим, так как за нас это уже сделали.
     */
    [PENDING]: {
      on: {
        [INIT]: INITIALIZING,
        [RENDER]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором компонент производит подготовку и инициализацию
     * рекламного места.
     */
    [INITIALIZING]: {
      on: {
        [RENDER]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором происходит перевызов отрисованного ранее баннера
     * с изначальными параметрами.
     *
     * Перевызов делается при перелистывании галерей и т.д.
     */
    [RELOADING_RENDERED]: {
      on: {
        [RENDER]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором происходит перевызов инициализированного ранее
     * рекламного места, но для которого баннер не пришел (`unexist`)
     * с изначальными параметрами.
     *
     * Перевызов делается при перелистывании галерей и т.д.
     */
    [RELOADING_NOT_EXIST]: {
      on: {
        [RENDER]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором происходит переинициализация отрисованного ранее
     * рекламного места с генерацией новых параметров и `divID`.
     */
    [REINITIALIZING_RENDERED]: {
      on: {
        [RENDER]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором происходит переинициализация инициализированного
     * ранее рекламного места, но для которого баннер не пришел (`unexist`)
     * с генерацией новых параметров и `divID`.
     */
    [REINITIALIZING_NOT_EXIST]: {
      on: {
        [RENDER]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором баннер был отрисован для рекламного места.
     */
    [RENDERED]: {
      on: {
        [RELOAD]: RELOADING_RENDERED,
        [REINIT]: REINITIALIZING_RENDERED,
        [RESIZE]: RENDERED,
        [UNEXIST]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором баннер не пришел (`unexist`) для рекламного места.
     */
    [NOT_EXIST]: {
      on: {
        [RELOAD]: RELOADING_NOT_EXIST,
        [REINIT]: REINITIALIZING_NOT_EXIST,
        [RESIZE]: NOT_EXIST,
        [UNMOUNT]: UNMOUNTED,
      },
    },

    /**
     * Состояние, в котором компонент размонтирован и мы должны закончить любую
     * активность.
     */
    [UNMOUNTED]: {},
  },
};

const placeholderStates = [
  IDLE,
  PENDING,
  INITIALIZING,
];

const fallbackStates = [
  NOT_EXIST,
  RELOADING_NOT_EXIST,
  REINITIALIZING_NOT_EXIST,
];

const compositeStates = placeholderStates.concat(fallbackStates);
const DefaultPlaceholder = () => <div className={styles.defaultPlaceholder} />;

/**
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 *
 * Компонент для конфигурирования рекламных мест через глобальный и локальный
 * конфиги.
 *
 * Глобальный конфиг указывается в пропсе `ads` у компонента `App`.
 *
 * Рекламные места поддерживают клиентский рендеринг,
 * необходимый для клиентской навигации в SPA.
 *
 * Алгоритм работы:
 *
 * 1. Если передан пропс `placeholder`, то до момента инициализации рекламного
 *    места отображаем переданный компонент.
 *
 * 1. После инициализации рекламного места и отрисовки баннера изначальные
 *    размеры убираются и уже баннер отвечает за отрисовку, но только в том
 *    случае если изменение размеров рекламного места не повлияет
 *    на пользовательский скрол.
 *
 * 1. Если рекламное место не было продано (баннер не отрисован) и указан
 *    пропс `fallback`, то отображаем переданный компонент.
 */

class Ad extends PureComponent {
  static propTypes = {
    /**
     * Уникальный id плейсхолдера.
     *
     * Если не указан, то он автоматически формируется на основе глобального
     * инкрементального счетчика.
     *
     * По нему будет инициализироваться баннер и он должен быть уникальным
     * в рамках страниц.
     */
    domId: PropTypes.string.isRequired,

    /**
     * Имя плейсхолдера для отображения баннера.
     *
     * На основе этого имени собирается конфигурация из параметров переданных
     * в компонент `App`.
     */
    name: PropTypes.string,

    /**
     * Кастомный id сайта.
     *
     * Если значение явно не указано у компонента, то с помощью
     * `withResolveProps` оно будет получено из параметров, переданных
     * в компонент `App` при их наличии.
     */
    siteId: PropTypes.string.isRequired,

    /**
     * Кастомный id рекламного места.
     *
     * Если значение явно не указано у компонента, то с помощью
     * `withResolveProps` оно будет получено из параметров переданных
     * в компонент `App` при их наличии.
     */
    blockId: PropTypes.string.isRequired,

    /**
     * Набор параметров для инициализации рекламного места.
     *
     * Явно переданные параметры будут смерджены в `withResolveProps`
     * с дефолтными параметрами для блока из глобальной конфигурации.
     */
    options: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])).isRequired,

    /**
     * Дополнительный набор параметров для инициализации рекламного места.
     *
     * Формируется в `withResolveProps` на основе `blockId` и `siteId`.
     */
    sspOptions: PropTypes.objectOf(PropTypes.string).isRequired,

    /**
     * Начальная ширина рекламного места.
     *
     * Используется для резервирования места под плейсхолдер куда позже будет
     * вставлен баннер. Такое резервирование позволит сократить до минимума
     * пересчет лейаута при отображении рекламы.
     *
     * По умолчанию это 100% ширины родителя.
     */
    width: PropTypes.number,

    /**
     * Начальная высота рекламного места.
     *
     * Используется для резервирования места под плейсхолдер, куда позже будет
     * вставлен баннер. Такое резервирование позволит сократить до минимума
     * пересчет лейаута при отображении рекламы.
     */
    height: PropTypes.number,

    /**
     * Параметр, отвечающий за способ инициализации рекламного места.
     *
     * При его выставлении инициализация будет делаться при доскролливании
     * до блока.
     */
    lazy: PropTypes.bool,

    /**
     * Параметр, отвечающий за использование серверного рендеринга
     * для инициализации рекламного места.
     *
     * Если значение явно не указано у компонента, то с помощью
     * `withResolveProps` оно будет получено из параметров переданных
     * в компонент `App` при их наличии.
     */
    ssr: PropTypes.bool,

    /**
     * Параметр, идентифицирующий контекст вызова в рамках страницы.
     *
     * Автоматически резолвится из контекста компонента `App`, но можно
     * указать ручками.
     */
    requestId: PropTypes.string,

    /**
     * Метод жизненного цикла рекламного места, вызываемый перед инициализацией
     * баннера.
     *
     * Код этой функции должен быть синхронным.
     */
    adWillLoad: PropTypes.func,

    /**
     * Метод жизненного цикла рекламного места, вызываемый перед удалением
     * рекламного места из DOM.
     *
     * Код этой функции должен быть синхронным.
     */
    adWillUnload: PropTypes.func,

    /**
     * Компонент, отображаемый до того, как баннер будет доступен пользователю.
     */
    placeholder: PropTypes.func,

    /**
     * Компонент, отображаемый пользователю в случае отсутствия баннера.
     */
    fallback: PropTypes.func,

    /** @ignore */
    isMobile: PropTypes.bool,

    /**
     * Рекламные метки для таргетирования.
     *
     * Подробнее можно прочитать по ссылкам в дата-резолвере.
     */
    puids: puidsPropTypes,

    /**
     * Пуиды, которые могут выставляться вручную для каждого рекламного места.
     */
    manualPuids: PropTypes.shape({
      puid44: PropTypes.string,
      puid39: PropTypes.string,
    }),

    /**
     * Функция для начала перехода компонента из одного состояния в другое.
     * На вход принимает событие. Переход будет совершен только если
     * он предусмотрен в карте переходов (см. `statechart`).
     */
    transition: PropTypes.func.isRequired,

    /**
     * Текущее состояние компонента по карте переходов.
     */
    machineState: PropTypes.oneOf([
      IDLE,
      PENDING,
      RENDERED,
      NOT_EXIST,
      UNMOUNTED,
      INITIALIZING,
      RELOADING_RENDERED,
      RELOADING_NOT_EXIST,
      REINITIALIZING_RENDERED,
      REINITIALIZING_NOT_EXIST,
    ]),

    /**
     * Отключение автоинициализации рекламы.
     * В этом случае будет создан контейнер для рекламы, но сама реклама
     * не будет вызвана. Для инициализации нужно будет отправить событие
     * init через PubSub и передать id контейнера банера.
     */
    disableAutoInit: PropTypes.bool,

    /**
     * Количество секунд, в течение которых
     * баннер будет прибит к верху (если он обернут в StickyPortal)
     * пока находится в поле зрения пользователя,
     * прежде чем кинет событие(на которое должен быть подпиан StickyPortal)
     * на то что он может быть "отклеен"
     */
    stickyTemp: PropTypes.number,

    /**
     * Функция для получения pageNumber из контекста
     */
    getPageNumber: PropTypes.func.isRequired,

    /**
     * Функция для обновления pageNumber в контексте
     */
    incrementPageNumber: PropTypes.func.isRequired,

    /**
     * Необходимо для получение X-Split заголовка и определение типа теста
     */
    requestHeaders: requestHeadersPropTypes,

    /**
     * Показываем только заглушки на страницах preview
     */
    hideOnPreview: PropTypes.bool,

    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  };

  static defaultProps = {
    lazy: false,
    placeholder: DefaultPlaceholder,
    fallback: DefaultPlaceholder,
  };

  constructor(props) {
    super(props);

    const {
      domId,
      blockId,
      sspOptions,
      getPageNumber,
      incrementPageNumber,
    } = this.props;

    this.pageNumber = getPageNumber(blockId);
    incrementPageNumber(blockId);

    this.unmounted = false;

    const options = this.getBannerOptions();

    const mode = this.getRenderingMode();

    const initialParamsString = JSON.stringify({
      mode,
      domId,
      options,
      sspOptions,
    });

    const content = process.env.BROWSER_RUNTIME
      ? document.querySelector(`#${domId}-placeholder`)?.innerHTML
      : undefined;

    this.state = {
      content,
      initialParamsString,
      resized: false,
    };
  }

  componentDidMount() {
    const {
      name,
      domId,
    } = this.props;

    /**
     * Для упрощения работы в остальных местах компонента где может
     * потребоваться sdk capirs мы загружаем его перед тем как
     * проинициализировать рантайм.
     */
    initAds().then(sdk => {
      this.sdk = sdk;
      if (!this.unmounted) this.initRuntime();
    }).catch(console.error);

    this.eventSubReload = PubSub.subscribe('reload', (topic, data) => {
      if (data.name === name) {
        if (topic === 'reload.block') this.reloadBanner();
        if (topic === 'reload.block_if_visible') this.reloadBannerIfVisible();
      }
    });

    this.eventSubInit = PubSub.subscribe('init', (topic, data) => {
      if (data.domId === domId) {
        /**
         * Если банера не существует, то это означает, что рантайм еще
         * не проиниализирован. В этом случае инициализация будет
         * произведена в `initRuntime`. Для этого выставляем флаг что банер
         * ожидает инициализации.
         */
        if (this.banner) {
          this.props.transition(INIT);
        } else {
          this.waiting = true;
        }
      }
    });
  }

  componentDidUpdate(prevProps) {
    const {
      requestId,
      transition,
      machineState,
    } = this.props;

    /**
     * При клиентской навигации, которую можно определить
     * по пропсу `requestId`, запускаем процесс переинициализации рекламного
     * места.
     */
    if (prevProps.requestId !== requestId) {
      /**
       * Если состояние было PENDING, то это значит, что рекламное место
       * было проинициализировано в `capirs-fast-init`, но еще не было вызвано.
       * В этом случае запускаем полноценную инициализацию через компонент.
       */
      if (machineState === PENDING) {
        if (!this.unmounted) this.initRuntime();
      } else {
        transition(REINIT);
      }
    }
  }

  componentWillUnmount() {
    if (this.props.stickyTemp) {
      this.begunInViewUnregister();
    }
    this.props.transition(UNMOUNT);
    this.eventSubReload && PubSub.unsubscribe(this.eventSubReload);
    this.eventSubInit && PubSub.unsubscribe(this.eventSubInit);
    this.resizeObserver && this.resizeObserver.unobserve(this.banner);
    this.resizeTimeoutId && clearTimeout(this.resizeTimeoutId);
    this.unmounted = true;
  }

  /**
   * Это колбек жизненного цикла из библиотеки `react-automata`.
   * Он предназначен для реализации сайд-эффектов, связанных с переходами
   * компонента из состояния в состояние.
   */
  componentDidTransition(prevMachineState, event) {
    const {
      domId,
      adWillUnload,
      transition,
      machineState,
      incrementPageNumber,
      getPageNumber,
      blockId,
      name,
    } = this.props;

    if (name === 'Shtorka' && machineState === RENDERED) {
      PubSub.publish(SHTORKA_SHOWED);
    }

    if (prevMachineState === machineState) {
      if (event === RESIZE) {
        this.resizeObserver && this.resizeObserver.unobserve(this.container);
        this.setState({ resized: true });
      }
      return;
    }

    switch (machineState) {
      case INITIALIZING:
        this.banner.setAttribute('id', domId);
        this.initCapirs();
        break;
      case RENDERED:
      case NOT_EXIST:
        /**
         * Теперь рекламное место контролируется компонентом `Ad` и из `body`
         * можно удалить класс, выставленный `capirs-fast-init.js`, чтоб
         * не применялись лишние стили.
         */
        document.body.classList.remove(`${domId}-rendered`);

        this.resizeObserver = new IntersectionObserver(entries => {
          const shouldResize = entries.some(entry => {
            if (this.unmounted) return false;
            return entry.boundingClientRect.bottom > entry.rootBounds?.height;
          });
          if (shouldResize) transition(RESIZE);
        }, { threshold: [0.0, 0.3, 0.7, 1.0] });

        this.resizeObserver.observe(this.container);
        break;
      case RELOADING_RENDERED:
      case RELOADING_NOT_EXIST:
      case REINITIALIZING_RENDERED:
      case REINITIALIZING_NOT_EXIST:
      case UNMOUNTED:
        this.resizeObserver && this.resizeObserver.unobserve(this.banner);
        this.resizeTimeoutId && clearTimeout(this.resizeTimeoutId);
        break;
      default:
        // noop для eslint
        break;
    }

    switch (event) {
      case RELOAD:
      case REINIT:
        this.pageNumber = getPageNumber(blockId);
        incrementPageNumber(blockId);

        this.resizeObserver.unobserve(this.container);
        this.setState({ resized: false });
        break;
      default:
        // noop для eslint
        break;
    }

    switch (event) {
      case RELOAD:
      case REINIT:
      case UNMOUNT:
        if (adWillUnload && this.sdk) adWillUnload(this.sdk);
        break;
      default:
        // noop для eslint
        break;
    }

    switch (event) {
      case RELOAD:
        this.reloadCapirs();
        break;
      case REINIT:
        this.banner.innerHTML = '';
        this.banner.setAttribute('id', domId);
        this.reinitCapirs();
        break;
      default:
        // noop для eslint
        break;
    }
  }

  begunInViewUnregister() {
    if (this.sdk) {
      this.sdk.Begun.Autocontext.Callbacks.unregister('inView', this.props.domId);
    }
  }

  /**
   * Из-за того что реклама (capirs) работает вне жизненного цикла
   * реакта + у нас используется `capirs-fast-init.js` для ускорения
   * инициализации необходимо как-то это все синхронизировать с жизненным
   * циклом компонента.
   *
   * Для этого мы после монтирования компонента на клиенте проверяем что же уже
   * было сделано с подконтрольным DOM и пытаемся провести синхронизацию
   * состояния.
   *
   * TODO: Подумать, как написать интеграционные тесты компонента с `capirs`
   * уж больно не предсказуемо sdk работает =(
   */
  initRuntime() {
    const {
      domId,
      transition,
      disableAutoInit,
    } = this.props;

    this.initEventHandlers();

    /**
     * Пытаемся найти в DOM рекламного места элемент с контейнером в который
     * инициализируется реклама.
     */
    const block = this.placeholder.querySelector(`#${domId}`);

    /**
     * Его наличие будет говорить о том что рекламное место уже кем-то
     * проинициализировано. Осталось понять кем.
     */
    if (block) {
      this.banner = block;

      const externalState = block.getAttribute(STATE_ATTR_NAME);

      /**
       * Если на контейнере баннера есть дескриптор состояния,
       * то это обозначает что инициализация была произведена
       * в `capirs-fast-init.js`.
       *
       * Либо рекламное место проинициализировано неизвестным нам способом.
       */
      if (externalState) {
        /**
         * Если рекламное место еще в процессе ожидания ответа от `capirs`,
         * то берем контроль на себя выставляя специальный атрибут.
         */
        if (externalState === PENDING) {
          block.setAttribute(PREVENT_EXTERNAL_ATTR_NAME, true);
        }
        transition(externalState);
      } else {
        transition(INIT);
      }
    } else {
      /**
       * Если контейнера баннера нет в DOM дереве, то создаем его и запускаем
       * полноценную инициализацию.
       *
       * Создаем контейнер баннера через DOM на прямую, а не через реакт
       * во избежания актуализации рендеринга и разметки, которая может
       * испортить биндинги и какие-то служебные атрибуты `capirs`.
       */
      this.banner = document.createElement('div');
      this.banner.setAttribute('id', domId);

      this.placeholder.innerHTML = '';
      this.placeholder.appendChild(this.banner);

      if (!disableAutoInit || this.waiting) {
        transition(INIT);
      }
    }
  }

  initEventHandlers() {
    const instance = this;
    const { Begun } = this.sdk;

    function draw() {
      /**
       * Эти переменные должны остаться тут, не нужно выносить их из этой
       * функции, иначе они не будут обновляться при перерисовке
       */
      const {
        domId,
        transition,
      } = instance.props;

      /**
       * TODO: Не забывать синхронизировать код поиска `id` рекламного места
       * с `capirs-fast-init.js`.
       * Для некоторых рекламных мест может в DOM выкидываться много
       * элементов с одинаковым id что требует дополнительной обработки.
       *
       * Так же если одно и тоже рекламное место вставлено несколько раз
       * на страницу, то у них будет один id: begun_block_{id}.
       * Поэтому мы ищем родителя через closest.
       * this.parentNode исползовать нельзя, т.к реклама может оборачивать
       * элемент еще чем-то.
       */
      const bannerParent = this.closest('div[data-capirs]');

      /**
       * Реклама может вставляться сторонними виджетами, например
       * рекомендательной системой. Мы эти банеры никак не обрабатываем
       */
      if (!bannerParent) return;

      const bannerId = bannerParent.firstChild.id;
      // Если компонент размонтирован, то отписываем все обработчики.
      if (instance.unmounted) {
        /**
         * TODO: Реализовать отписывание старых событий после реализации
         * https://jira.rambler-co.ru/browse/CAPIRS-53
         */
        // unregister('block', 'draw', draw);
        // unregister('block', 'unexist', unexist);
        return;
      }

      if (domId === bannerId) {
        if (instance.timeout) clearTimeout(instance.timeout);

        /**
         * Переходим в состояние отрисовки по истечении таймаута так как
         * в случаях отрисовки, но ошибке на стороне инициализации баннера
         * будет вызван обработчик `unexist`. Если это произойдет
         * в течении 500мс, то нет смысла показывать баннер.
         *
         * Задержка в 500мс была выявлена опытным путем и при необходимости
         * может быть как увеличена так и уменьшена.
         *
         * При отключенном кеше в браузере за это время (500мс)
         * событие `unexist` может не успеть прийти. Это ок и в любом случае
         * нужно смотреть как все будет работать с кешом.
         */
        instance.timeout = setTimeout(() => transition(RENDER), 500);
      }
    }

    function drawComplete({ status, viewTypes, bannerPlace }) {
      const bannerId = bannerPlace && bannerPlace.id;

      const {
        transition,
        domId,
      } = instance.props;

      if (domId === bannerId) {
        if (!status) {
          transition(UNEXIST);
          return;
        }

        /**
         * Не забывать синхронизировать добавление класса с `capirs-fast-init.js`.
         */
        if (viewTypes.split(',').indexOf('Branding') > -1) {
          document.body.classList.add(BRANDING_CLASSNAME);
          transition(RESIZE);
        }
      }
    }

    function unexist(feed, blockData) {
      /**
       * Эти переменные должны остаться тут, не нужно выносить их из этой
       * функции, иначе они не будут обновляться при перерисовке
       */
      const {
        domId,
        transition,
        disableAutoInit,
        stickyTemp,
      } = instance.props;

      const bannerId = blockData.div_id;

      // Если компонент размонтирован, то отписываем все обработчики.
      if (instance.unmounted) {
        /**
         * TODO: Реализовать отписывание старых событий после реализации
         * https://jira.rambler-co.ru/browse/CAPIRS-53
         */
        // unregister('block', 'draw', draw);
        // unregister('block', 'unexist', unexist);
        return;
      }

      if (domId === bannerId) {
        if (instance.timeout) clearTimeout(instance.timeout);
        /**
         * Если рекламы не существует, то удаляем слайд с этой реклмамой
         * из галереи.
         */
        if (disableAutoInit) {
          PubSub.publish('slide.remove', { domId });
        }
        if (stickyTemp) {
          PubSub.publish(`sticky.remove.${domId}`);
          instance.begunInViewUnregister();
        }
        transition(UNEXIST);
      }
    }

    /**
     * Оказалось что промис интерфейс не работает не так же как колбеки `draw`
     * с `unexist` и в некоторых кейсах не может использоваться. Из-за этого
     * переделали на работу с колбеками.
     */
    Begun.Autocontext.Callbacks.register({ block: { draw, drawComplete, unexist } });

    const {
      stickyTemp,
      domId,
    } = instance.props;

    if (stickyTemp) {
      Begun.Autocontext.Callbacks.register({
        'inView': {
          [domId]: ({ totalInViewTime }) => {
            if (totalInViewTime > stickyTemp) {
              PubSub.publish(`sticky.remove.${domId}`);
              instance.begunInViewUnregister();
            }
          },
        },
      });
    }
  }

  initCapirs(mode = this.getRenderingMode()) {
    const {
      domId,
      sspOptions,
      adWillLoad,
    } = this.props;

    const { Adf } = this.sdk;
    if (adWillLoad) adWillLoad(this.sdk);

    /**
     * При передаче параметров в `Adf.banner` обязательно делаем их копию
     * так как sdk при инициализации мутирует переданные объекты. Нам эта
     * магия не нужна.
     */
    Adf.banner[mode](domId, { ...this.getBannerOptions() }, {
      ...sspOptions,
      publisher_uid: getCookie(USER_ID_COOKIE),
    });
  }

  reinitCapirs() {
    this.initCapirs();
  }

  reloadCapirs() {
    this.initCapirs('reloadssp');
  }

  bannerRendered() {
    const bannerContent = this.banner.querySelector('[id^="begun_block"]');
    return !!(bannerContent && bannerContent.children.length);
  }

  reloadBanner() {
    this.props.transition(RELOAD);
  }

  reloadBannerIfVisible() {
    if (this.unmounted) return;

    const {
      top,
      height,
      bottom,
    } = this.container.getBoundingClientRect();

    if (top > -height * 0.4 && bottom <= document.documentElement.clientHeight) {
      this.reloadBanner();
    }
  }

  getRenderingMode() {
    const {
      lazy,
      isMobile,
    } = this.props;

    if (!lazy) return 'ssp';
    if (isMobile) return 'sspScrollMobile';
    return 'sspScroll';
  }

  /**
   * Подмешиваем pageNumber к опциям.
   * Подробнее про него здесь - https://confluence.rambler-co.ru/pages/viewpage.action?pageId=37355502
   */
  getBannerOptions() {
    return {
      pageNumber: this.pageNumber,
      ...this.props.options,
    };
  }

  render() {
    const {
      domId,
      width,
      height,
      machineState,
      fallback: Fallback,
      placeholder: Placeholder,
      disableAutoInit,
      name,
      hideOnPreview,
      location: {
        pathname,
      },
    } = this.props;

    const {
      content,
      resized,
      initialParamsString,
    } = this.state;

    let showFallback = false;
    let showPlaceholder = false;

    /**
     * Если в качестве `Fallback` и `Placeholder` был передан один и тот
     * же компонент, то во избежания лишних перерисовок при смене состояний
     * мы переключаем отрисовку в "сложный" режим, когда всегда отрисовываем
     * лишь один компонент. Его отрисовка делается по объединённым правилам.
     *
     * P.S. Для остальных случаев обычно нужно использовать копонент `Action`
     * из `react-automata`.
     */
    if (Fallback === Placeholder) {
      showPlaceholder = !!Placeholder
        && compositeStates.includes(machineState);
    } else {
      showFallback = !!Fallback && fallbackStates.includes(machineState);
      showPlaceholder = !!Placeholder
        && placeholderStates.includes(machineState);
    }

    let scoped;

    /**
     * Небольшая оптимизация.
     * Генерируем стили только если мы в состоянии `IDLE`.
     */
    if (machineState === IDLE) {
      /**
       * Так как скрипт `capirs-fast-init.js` не может вмешиваться в разметку
       * подконтрольную реакту, но должен сделать видимым рекламное место
       * в случае отрисовки баннера до инициализации компонента мы формируем
       * специальные глобальные стили которые помогут это сделать за счет
       * добавления на `body` специального класса.
       */
      scoped = resolveScopedStyles((
        <scope>
          <style jsx global>{`
            .${domId}-rendered #${domId}-container
              & > :not(#${domId}-placeholder)
                display none

              #${domId}-placeholder
                position static
                opacity 1
          `}</style>
        </scope>
      ));
    }

    const isAdVisible =  (height && !resized && (showFallback || showPlaceholder))
      || machineState === RENDERED
      || machineState === REINITIALIZING_RENDERED;
    const showStub = hideOnPreview && pathname.includes('/preview/');

    return (
      <div className={cx(styles.wrapper, isAdVisible && 'lb-ad-visible')}
        {...{
          [RENDER_STATE_ATTR_NAME]: machineState,
        }}
      >
        {scoped && (
          <scoped.styles />
        )}
        <div
          id={`${domId}-container`}
          className={styles.container}
          ref={node => (this.container = node)}
          {...!resized && { style: { minWidth: width, minHeight: height } }}
        >
          {(showPlaceholder || showStub) && (
            <Placeholder />
          )}
          {showFallback && (
            <Fallback />
          )}
          {!showStub && <div
            id={`${domId}-placeholder`}
            ref={node => (this.placeholder = node)}
            className={styles.banner}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: content }}
            {...{
              [OPTIONS_ATTR_NAME]: initialParamsString,
              [DISABLE_ATTR_NAME]: disableAutoInit,
              [NAME_ATTR_NAME]: name.toLowerCase(),
            }}
          />}
        </div>
      </div>
    );
  }
}

const AdWithHOCs = compose(
  withRouter,
  /**
   * Не рендерим рекламу:
   * - На страницах: 404, aвтора, about
   * - При активной прайм подписке. Информацию об активной подписке мы
   * получаем через некоторое время после загрузки страницы. За это время
   * реклама может успеть вызваться. Это не страшно, т.к capirs отключает
   * рекламу на своей стороне.
   *
   */
  getContext({
    ads: adsPropTypes(),
    mediaUrlPrefix: PropTypes.string,
    splitTest: PropTypes.object,
  }),
  branch(
    ({ location, staticContext, mediaUrlPrefix }) => {
      const { appStatus } = location.state || staticContext || {};
      const isError = isError4xx(appStatus);
      const excludedPages = [aboutRoute.path, authorRoute.path, offlineRoute.path]
        .some(path => matchPath(location.pathname, {
          path: [path, `${mediaUrlPrefix}${path}`],
        }));

      return isError || excludedPages || (process.env.BROWSER_RUNTIME && !navigator.onLine);
    },
    renderNothing
  ),
  withRequestHeaders,
  withPuids,
  withRequestId,
  withBreakpoint,
  withResolveProps,
  withResolveDomId,
  withPageNumbers,
  withStatechart(statechart),
)(Ad);

AdWithHOCs.displayName = 'Ad';

export { Ad as StorybookComponent };
export default AdWithHOCs;
